import React, { useEffect, useRef, useState } from "react";
import Chart from "chart.js/auto";
import "chartjs-adapter-date-fns";
import { Box, Typography } from "@mui/material";

// Register labels mapping
const REGISTER_LABELS = {
  // Main Field
  // 40506: "Main Comp Actual Flow Rate",
  FLOW_METER: "Main Comp Actual Flow Rate",

  // 41579: "Auto Flow Rate Setpoint",
  FLOW_RATE_SP: "Auto Flow Rate Setpoint",
  41001: "Field Control Setpoint",
  41002: "Field Control Deadband",
  41714: "Hi Field Control Override SP",
  41715: "Hi Field Control Override DB",

  // Main Flow additional
  // 40505: "Discharge PSI Actual",
  DISCHARGE_PRES: "Discharge PSI Actual",
  41517: "Sample Time",
  41652: "Sample DB",
  41653: "Increment SP",
  41654: "Decrement DB",
  41717: "Min Flow Rate",
  41716: "Max Flow Rate",

  // PLG1
  40510: "Tubing Pressure",
  42157: "Tubing : Casing SetPoint %",
  40511: " Flow Rate",
  43808: " Auto Flow Rate SP",
  40509: " Casing",
  44035: " Sample Time",
  44023: " Sample DB",
  44019: " Increment SP",
  44015: " Decrement DB",
  41719: " Min Flow Rate",
  41718: " Max Flow Rate",
  42290: " Overrun SP H",
  42291: " Overrun SP M",
  42292: " Overrun SP S",
  42152: " Increment SP",
  42296: " Underrun SP H",
  42297: " Underrun SP M",
  42298: " Underrun SP S",
  42150: " Decrement SP",
  42868: " Min Flow Rate SP",
  42872: " Max Flow Rate SP",

  // PLG2
  40516: "Tubing Pressure",
  42168: "Tubing : Casing SetPoint %",
  40517: " Flow Rate",
  43856: " Auto Flow Rate SP",
  40515: " Casing",
  44036: " Sample Time",
  44024: " Sample DB",
  44020: " Increment SP",
  44016: " Decrement DB",
  41721: " Min Flow Rate",
  41720: " Max Flow Rate",
  42317: " Overrun SP H",
  42318: " Overrun SP M",
  42319: " Overrun SP S",
  42163: " Increment SP",
  42323: " Underrun SP H",
  42324: " Underrun SP M",
  42325: " Underrun SP S",
  42161: " Decrement SP",
  42869: " Min Flow Rate SP",
  42873: " Max Flow Rate SP",

  // PLG3
  40519: "Tubing Pressure",
  42179: "Tubing : Casing SetPoint %",
  40520: " Flow Rate",
  43904: " Auto Flow Rate SP",
  40518: " Casing",
  44037: " Sample Time",
  44025: " Sample DB",
  44021: " Increment SP",
  44017: " Decrement DB",
  41723: " Min Flow Rate",
  41722: " Max Flow Rate",
  42344: " Overrun SP H",
  42345: " Overrun SP M",
  42346: " Overrun SP S",
  42174: " Increment SP",
  42353: " Underrun SP H",
  42354: " Underrun SP M",
  42355: " Underrun SP S",
  42172: " Decrement SP",
  42870: " Min Flow Rate SP",
  42874: " Max Flow Rate SP",

  //PLG4
  40522: "Tubing Pressure",
  42190: "Tubing : Casing SetPoint %",
  40523: " Flow Rate",
  43952: " Auto Flow Rate SP",
  40521: " Casing",
  44038: " Sample Time",
  44026: " Sample DB",
  44022: " Increment SP",
  44018: " Decrement DB",
  41725: " Min Flow Rate",
  41724: " Max Flow Rate",
  42371: " Overrun SP H",
  42372: " Overrun SP M",
  42373: " Overrun SP S",
  42185: " Increment SP",
  42377: " Underrun SP H",
  42378: " Underrun SP M",
  42379: " Underrun SP S",
  42183: " Decrement SP",
  42871: " Min Flow Rate SP",
  42875: " Max Flow Rate SP",
};

// Color palette for charts
const COLORS = [
  "#1f77b4",
  "#ff7f0e",
  "#2ca02c",
  "#d62728",
  "#9467bd",
  "#8c564b",
  "#e377c2",
  "#7f7f7f",
  "#bcbd22",
  "#17becf",
  "#393b79",
  "#637939",
  "#8c6d31",
  "#843c39",
  "#ad494a",
  "#5254a3",
  "#6b6ecf",
  "#9c9ede",
  "#8ca252",
  "#b5cf6b",
  "#cedb9c",
  "#e7ba52",
  "#e7cb94",
  "#e7969c",
  "#de9ed6",
];

const DEFAULT_VISIBLE_LABELS = new Set([
  "casing",
  "tubing pressure",
  "flow rate",
]);

const normalizeLabel = (label = "") => label.trim().toLowerCase();

const isDefaultVisibleSeries = (label) => {
  return DEFAULT_VISIBLE_LABELS.has(normalizeLabel(label));
};

const pad2 = (value) =>
  String(Math.max(0, Number(value) || 0)).padStart(2, "0");

const formatHHMMSS = (hours, minutes, seconds) => {
  if (
    hours === null ||
    hours === undefined ||
    minutes === null ||
    minutes === undefined ||
    seconds === null ||
    seconds === undefined
  ) {
    return null;
  }

  return `${pad2(hours)}:${pad2(minutes)}:${pad2(seconds)}`;
};

export default function HistoryChartManager({ chartData, selectedGroup }) {
  const chartRef = useRef(null);
  const chartInstanceRef = useRef(null);
  const [fontSize, setFontSize] = useState(10);

  useEffect(() => {
    const updateFontSize = () => {
      if (window.innerWidth < 600) setFontSize(8);
      else if (window.innerWidth < 960) setFontSize(9);
      else setFontSize(10);
    };
    updateFontSize();
    window.addEventListener("resize", updateFontSize);
    return () => window.removeEventListener("resize", updateFontSize);
  }, []);

  useEffect(() => {
    if (!chartRef.current || !chartData) return;

    // Destroy existing chart
    if (chartInstanceRef.current) {
      chartInstanceRef.current.destroy();
      chartInstanceRef.current = null;
    }

    // Parse chart data - handle the response structure
    let labels, registerKeys;

    if (chartData.time && Array.isArray(chartData.time)) {
      // Convert timestamps to Date objects
      labels = chartData.time.map((t) => new Date(t * 1000));
      // Get all keys except 'time'
      registerKeys = Object.keys(chartData).filter((key) => key !== "time");
    } else {
      console.error("Invalid chartData format:", chartData);
      return;
    }

    if (registerKeys.length === 0 || labels.length === 0) {
      console.warn("No data to display");
      return;
    }

    //console.log("📊 Creating chart with registers:", registerKeys);
    //console.log("📊 Data points:", labels.length);

    // Create datasets dynamically
    // const datasets = registerKeys.map((key, index) => {
    //   const registerNum = key;
    //   const label = REGISTER_LABELS[registerNum] || `Register ${registerNum}`;
    //   const color = COLORS[index % COLORS.length];

    //   return {
    //     label: label,
    //     borderColor: color,
    //     backgroundColor: color.replace(")", ", 0.1)").replace("rgb", "rgba"),
    //     data: chartData[key] || [],
    //     pointRadius: 1.5,
    //     tension: 0.1,
    //     borderWidth: 2,
    //     fill: false,
    //   };
    // });
    console.log("*************:", {
      timePoints: chartData.time.length,
      registers: chartData,
      firstTimestamp: new Date(chartData.time[0] * 1000).toLocaleString(),
      lastTimestamp: new Date(
        chartData.time[chartData.time.length - 1] * 1000,
      ).toLocaleString(),
    });
    // ---------------------------------------------------------
    // Find the H / M / S registers used for grouped tooltip data
    // ---------------------------------------------------------
    const groupedTimeRegisters = {
      overrun: {
        h: null,
        m: null,
        s: null,
      },
      underrun: {
        h: null,
        m: null,
        s: null,
      },
    };

    registerKeys.forEach((key) => {
      const label = (REGISTER_LABELS[key] || "").trim();

      if (label === "Overrun SP H") groupedTimeRegisters.overrun.h = key;
      if (label === "Overrun SP M") groupedTimeRegisters.overrun.m = key;
      if (label === "Overrun SP S") groupedTimeRegisters.overrun.s = key;

      if (label === "Underrun SP H") groupedTimeRegisters.underrun.h = key;
      if (label === "Underrun SP M") groupedTimeRegisters.underrun.m = key;
      if (label === "Underrun SP S") groupedTimeRegisters.underrun.s = key;
    });

    // ---------------------------------------------------------
    // Create datasets
    // ---------------------------------------------------------
    const isPLGGroup = selectedGroup?.startsWith("PLG");
    const datasets = registerKeys
      .map((key, index) => {
        const registerNum = key;
        const label = REGISTER_LABELS[registerNum] || `Register ${registerNum}`;
        const normalizedLabel = normalizeLabel(label);
        const cleanLabel = label.trim();

        const color = COLORS[index % COLORS.length];

        // Keep the original array indexes aligned with chartData.time.
        // Do NOT filter nulls because filtering shifts the timestamps.
        const data = chartData[key] || [];

        if (!data.some((value) => value !== null && value !== undefined)) {
          return null;
        }

        const isGroupedTimePart =
          cleanLabel === "Overrun SP H" ||
          cleanLabel === "Overrun SP M" ||
          cleanLabel === "Overrun SP S" ||
          cleanLabel === "Underrun SP H" ||
          cleanLabel === "Underrun SP M" ||
          cleanLabel === "Underrun SP S";

        return {
          label: cleanLabel,
          borderColor: color,
          backgroundColor: color,
          data,
          pointRadius: 1.5,
          tension: 0.1,
          borderWidth: 2,
          fill: false,

          // Only Casing, Tubing Pressure and Flow Rate
          // are selected when the chart initially loads.
          hidden: isPLGGroup ? !isDefaultVisibleSeries(cleanLabel) : false,

          // Used by the legend filter below.
          _isGroupedTimePart: isGroupedTimePart,

          // Keep the register key available internally.
          _registerKey: key,

          // Prevent H/M/S from appearing as individual tooltip entries.
          _skipIndividualTooltip: isGroupedTimePart,
        };
      })
      .filter(Boolean);
    // Create new chart
    const ctx = chartRef.current.getContext("2d");
    chartInstanceRef.current = new Chart(ctx, {
      type: "line",
      data: {
        labels: labels,
        datasets: datasets,
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
          intersect: false,
          mode: "index",
        },
        events: ["mousemove", "mouseout", "click", "touchstart", "touchmove"],

        onHover: (event, activeElements, chart) => {
          // No chart element is being hovered.
          // This normally means the mouse moved to the legend area.
          if (!activeElements || activeElements.length === 0) {
            chart.tooltip.setActiveElements([], {
              x: 0,
              y: 0,
            });

            chart.update("none");
          }
        },
        scales: {
          x: {
            type: "time",
            time: {
              tooltipFormat: "MM/dd/yyyy hh:mm a",
              displayFormats: {
                minute: "hh:mm a",
                hour: "MM/dd hh:mm a",
                day: "MM/dd/yyyy",
              },
            },
            ticks: {
              color: "white",
              maxRotation: 45,
              maxTicksLimit: window.innerWidth < 600 ? 4 : 8,
              font: {
                size: fontSize,
              },
            },
            title: {
              display: true,
              text: "Date & Time",
              color: "white",
              font: {
                size: fontSize + 2,
              },
            },
            grid: {
              color: "rgba(255,255,255,0.2)",
            },
          },
          y: {
            type: "linear",
            display: true,
            position: "left",
            ticks: {
              color: "white",
              font: {
                size: fontSize,
              },
            },
            title: {
              display: true,
              text: "Values",
              color: "white",
              font: {
                size: fontSize + 2,
              },
            },
            grid: {
              color: "rgba(255,255,255,0.2)",
            },
          },
        },
        plugins: {
          legend: {
            position: window.innerWidth < 960 ? "bottom" : "right",
            align: "center",
            labels: {
              color: "white",
              font: {
                size: fontSize,
              },
              boxWidth: 15,
              padding: 8,
              filter: (legendItem, data) => {
                const dataset = data.datasets[legendItem.datasetIndex];

                return !dataset?._isGroupedTimePart;
              },
            },
            // IMPORTANT:
            // When moving from the chart to the legend, clear the tooltip.
            onHover: (event, legendItem, legend) => {
              const chart = legend.chart;

              chart.tooltip.setActiveElements([], {
                x: 0,
                y: 0,
              });

              chart.update("none");
            },

            onLeave: (event, legendItem, legend) => {
              const chart = legend.chart;

              chart.tooltip.setActiveElements([], {
                x: 0,
                y: 0,
              });

              chart.update("none");
            },
          },
          title: {
            display: true,
            // text: `${selectedGroup || "Register"} Data Time Series`,
            color: "white",
            font: {
              size: window.innerWidth < 600 ? 12 : 16,
              weight: "bold",
            },
          },
          tooltip: {
            backgroundColor: "rgba(0,0,0,0.9)",
            titleColor: "white",
            bodyColor: "white",
            borderColor: "rgba(255,255,255,0.3)",
            borderWidth: 1,
            titleFont: {
              size: fontSize + 1,
              weight: "bold",
            },
            bodyFont: {
              size: fontSize,
            },
            padding: 10,
            displayColors: true,
            callbacks: {
              // -----------------------------------------------------
              // Hide individual Overrun/Underrun H/M/S datasets
              // -----------------------------------------------------
              filter: (context) => {
                return !context.dataset._skipIndividualTooltip;
              },

              // -----------------------------------------------------
              // Add grouped Overrun / Underrun HH:MM:SS
              // -----------------------------------------------------
              afterBody: (tooltipItems) => {
                if (!tooltipItems || tooltipItems.length === 0) {
                  return [];
                }

                const dataIndex = tooltipItems[0].dataIndex;

                const groupedLines = [];

                // ---------------------------
                // Overrun
                // ---------------------------
                const overrun = groupedTimeRegisters.overrun;

                if (
                  overrun.h !== null &&
                  overrun.m !== null &&
                  overrun.s !== null
                ) {
                  const overrunValue = formatHHMMSS(
                    chartData[overrun.h]?.[dataIndex],
                    chartData[overrun.m]?.[dataIndex],
                    chartData[overrun.s]?.[dataIndex],
                  );

                  if (overrunValue !== null) {
                    groupedLines.push(`Overrun SP: ${overrunValue}`);
                  }
                }

                // ---------------------------
                // Underrun
                // ---------------------------
                const underrun = groupedTimeRegisters.underrun;

                if (
                  underrun.h !== null &&
                  underrun.m !== null &&
                  underrun.s !== null
                ) {
                  const underrunValue = formatHHMMSS(
                    chartData[underrun.h]?.[dataIndex],
                    chartData[underrun.m]?.[dataIndex],
                    chartData[underrun.s]?.[dataIndex],
                  );

                  if (underrunValue !== null) {
                    groupedLines.push(`Underrun SP: ${underrunValue}`);
                  }
                }

                return groupedLines;
              },
              label: function (context) {
                let label = context.dataset.label || "";
                if (label) {
                  label += ": ";
                }
                if (context.parsed.y !== null) {
                  label += context.parsed.y.toFixed(2);
                }
                return label;
              },
            },
          },
        },
      },
    });

    return () => {
      if (chartInstanceRef.current) {
        chartInstanceRef.current.destroy();
        chartInstanceRef.current = null;
      }
    };
  }, [chartData, fontSize, selectedGroup]);

  // Helper to get display info
  const getDisplayInfo = () => {
    if (!chartData || !chartData.time || chartData.time.length === 0)
      return null;

    return {
      count: chartData.time.length,
      start: new Date(
        chartData.time[chartData.time.length - 1] * 1000,
      ).toLocaleString(),
      end: new Date(chartData.time[0] * 1000).toLocaleString(),
      registers: Object.keys(chartData).filter((k) => k !== "time").length,
    };
  };

  const displayInfo = getDisplayInfo();

  return (
    <Box
      sx={{
        width: { xs: "100%", sm: "100%", md: "100%" },
        maxWidth: "100%",
        height: { xs: "60vh", sm: "450px", md: "67vh" },
        minHeight: { xs: "100%", sm: "400px" },
        background: "#1e293b",
        padding: { xs: 0.5, sm: 1.5, md: 2 },
        borderRadius: 2,
        mt: { xs: 1, sm: 2 },
        boxSizing: "border-box",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          width: "100%",
          height: displayInfo ? "calc(100% - 70px)" : "calc(100% - 10px)",
          minHeight: "200px",
          position: "relative",
        }}
      >
        <canvas
          ref={chartRef}
          style={{
            width: "100%",
            height: "100%",
            display: "block",
            maxWidth: "100%",
          }}
        />
      </Box>

      {displayInfo && (
        <Box
          sx={{
            color: "white",
            mt: { xs: 0.5, sm: 1 },
            pt: { xs: 0.5, sm: 1 },
            borderTop: "1px solid rgba(255,255,255,0.1)",
            fontSize: { xs: "9px", sm: "11px", md: "12px" },
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            width: "100%",
            justifyContent: "space-between",
            alignItems: { xs: "flex-start", sm: "center" },
            gap: { xs: 0.3, sm: 1 },
            px: { xs: 0.5, sm: 0 },
          }}
        >
          <Typography
            component="span"
            sx={{
              fontSize: "inherit",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            Start: {displayInfo.start}
          </Typography>
          {/* <Typography
            component="span"
            sx={{ fontSize: "inherit", fontWeight: 600 }}
          >
            Points: {displayInfo.count} | Registers: {displayInfo.registers}
          </Typography> */}
          <Typography
            component="span"
            sx={{
              fontSize: "inherit",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            End: {displayInfo.end}
          </Typography>
        </Box>
      )}
    </Box>
  );
}
